/**
 * Referral Landing — public page at /referral (generic) and /r/:code (personalised).
 *
 * The generic landing sells the referral program itself. When a code is
 * present, we resolve the referrer's name/avatar and personalise the copy
 * ("Suraj invited you") — proven conversion lift on referral funnels.
 */
import React from "react";
import { Link, useParams } from "react-router-dom";
import { HelmetProvider, Helmet } from "react-helmet-async";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { BrandMark } from "@/components/shared/BrandMark";
import {
  Gift, Award, ArrowRight, Sparkles, TrendingUp, Trophy, Copy, Check,
  MessageCircle, Share2,
} from "lucide-react";

interface ResolvedReferrer {
  referral_code: string;
  referrer_name?: string | null;
  referrer_picture?: string | null;
  paid_count?: number;
  free_days_earned?: number;
  cash_tier_unlocked?: boolean;
}

export default function ReferralLanding() {
  const { code } = useParams<{ code?: string }>();
  const q = useQuery<ResolvedReferrer | null>({
    queryKey: ["ref-resolve", code],
    enabled: !!code,
    queryFn: async () => {
      try {
        const r = await api.get(`/referrals/public/resolve/${encodeURIComponent(code!)}`);
        return r.data as ResolvedReferrer;
      } catch {
        return null;
      }
    },
  });

  const referrer = q.data;
  const [copied, setCopied] = React.useState(false);
  const landingUrl = code ? `${window.location.origin}/r/${code}` : `${window.location.origin}/referral`;

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(landingUrl);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1600);
    } catch { /* ignore */ }
  };

  return (
    <HelmetProvider>
      <Helmet>
        <title>{referrer?.referrer_name ? `${referrer.referrer_name} invited you to SociBaba` : "Refer & Earn — SociBaba"}</title>
        <meta name="description" content="Get 3x ROI on your first month or 6 months free. Backed by SociBaba's Signal-to-Action Engine and Money Meter." />
      </Helmet>

      <div className="min-h-screen bg-[color:var(--sb-bg,#FDFBF7)] text-[color:var(--sb-text,#0A1128)]">
        {/* Header */}
        <header className="max-w-7xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <Link to="/" data-testid="ref-logo">
            <BrandMark showIcon iconClassName="h-9 w-9 dark:invert" className="font-serif-display text-lg font-bold" />
          </Link>
          <div className="flex items-center gap-2">
            <Link to="/login" className="text-sm text-[color:var(--sb-text-muted)] hover:text-[color:var(--sb-text)]">Sign in</Link>
            <Link to="/signup" data-testid="ref-signup" className="rounded-full px-4 py-2 bg-[color:var(--sb-saffron,#F97316)] text-white text-sm font-semibold">Get started</Link>
          </div>
        </header>

        {/* Hero */}
        <section className="max-w-5xl mx-auto px-4 sm:px-6 pt-10 sm:pt-16 pb-8 text-center">
          {referrer?.referrer_name ? (
            <div className="inline-flex items-center gap-3 rounded-full border border-[color:var(--sb-border)] bg-[color:var(--sb-surface)] pl-2 pr-4 py-1.5 mb-6" data-testid="ref-personal-chip">
              {referrer.referrer_picture ? (
                <img src={referrer.referrer_picture} alt={referrer.referrer_name} loading="lazy" decoding="async" className="h-7 w-7 rounded-full"/>
              ) : (
                <div className="h-7 w-7 rounded-full bg-[color:var(--sb-saffron,#F97316)] text-white text-xs font-bold flex items-center justify-center">
                  {(referrer.referrer_name || "S").slice(0, 1)}
                </div>
              )}
              <span className="text-sm text-[color:var(--sb-text)]">
                <b>{referrer.referrer_name}</b> invited you to SociBaba
              </span>
            </div>
          ) : (
            <div className="inline-flex items-center gap-2 rounded-full border border-[color:var(--sb-border)] bg-[color:var(--sb-surface)] px-3 py-1.5 mb-6 text-[11px] uppercase tracking-[0.24em] font-semibold text-[color:var(--sb-saffron,#F97316)]">
              <Gift className="h-3 w-3" strokeWidth={2.5}/> Refer · Earn · Repeat
            </div>
          )}
          <h1 className="font-serif-display font-bold text-4xl sm:text-5xl lg:text-6xl leading-[1.05]">
            Earn <span className="text-[color:var(--sb-saffron,#F97316)]">₹500</span> per referral. Unlock <span className="text-emerald-600 dark:text-emerald-400">cash payouts</span> at 5.
          </h1>
          <p className="mt-4 text-base sm:text-lg text-[color:var(--sb-text-muted)] max-w-3xl mx-auto">
            {referrer?.referrer_name
              ? `${referrer.referrer_name} thinks SociBaba will help you. Join through their link and both of you get rewarded — plus SociBaba's 90-day 3x-ROI guarantee.`
              : "Share your custom SociBaba link. Every paid signup earns you ₹500 cash + a free month of Pro. Hit 5 paid referrals and unlock the cash tier — direct bank transfers, monthly."}
          </p>
          <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-3">
            <Link to="/signup" data-testid="ref-hero-primary" className="w-full sm:w-auto rounded-full px-7 py-4 bg-[color:var(--sb-saffron,#F97316)] text-white text-base font-bold hover:bg-[color:var(--sb-saffron-hover,#EA580C)] transition-colors inline-flex items-center justify-center gap-2 shadow-xl shadow-[color:var(--sb-saffron,#F97316)]/25">
              {referrer?.referrer_name ? `Join through ${referrer.referrer_name.split(" ")[0]}` : "Claim your referral link"} <ArrowRight className="h-4 w-4"/>
            </Link>
            <button onClick={copy} data-testid="ref-copy-link" className="rounded-full px-6 py-4 border border-[color:var(--sb-border)] bg-[color:var(--sb-surface)] text-sm font-medium inline-flex items-center gap-2 hover:border-[color:var(--sb-saffron,#F97316)]/40">
              {copied ? <><Check className="h-4 w-4 text-emerald-600"/> Copied!</> : <><Copy className="h-4 w-4"/> Copy this page's URL</>}
            </button>
          </div>
        </section>

        {/* 3x guarantee callout */}
        <section className="max-w-5xl mx-auto px-4 sm:px-6 py-8">
          <div className="rounded-3xl border border-[color:var(--sb-border)] bg-gradient-to-br from-emerald-500/10 to-fuchsia-500/10 p-6 sm:p-10 relative overflow-hidden" data-testid="ref-guarantee">
            <div className="absolute -top-16 -right-16 h-40 w-40 rounded-full bg-emerald-500/20 blur-3xl"/>
            <div className="relative flex flex-col sm:flex-row items-start sm:items-center gap-4">
              <div className="h-14 w-14 rounded-2xl bg-emerald-500 text-white flex items-center justify-center shrink-0 shadow-lg shadow-emerald-500/30">
                <Award className="h-7 w-7" strokeWidth={2.5}/>
              </div>
              <div className="flex-1">
                <div className="text-[10px] uppercase tracking-[0.24em] font-semibold text-emerald-800 dark:text-emerald-200">SibaBoost Guarantee</div>
                <div className="mt-1 font-serif-display text-2xl sm:text-3xl font-bold leading-tight">
                  3x ROI in 90 days — or your next <span className="text-emerald-700 dark:text-emerald-300">6 months are free</span>.
                </div>
                <div className="mt-2 text-sm text-[color:var(--sb-text-muted)] max-w-2xl">
                  Tracked live on the Money Meter — every rupee we recover for your business is traceable to the exact social signal it came from. If we can't 3x you, we don't deserve your money.
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* How it works */}
        <section className="max-w-6xl mx-auto px-4 sm:px-6 py-10">
          <div className="text-center mb-8">
            <div className="text-[11px] uppercase tracking-[0.32em] font-semibold text-[color:var(--sb-text-muted)]">How it works</div>
            <h2 className="mt-2 font-serif-display font-bold text-3xl sm:text-4xl">Three steps. Zero paperwork.</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Step n={1} icon={Share2} title="Share your link" body="Grab your unique /r/{yourname} URL. Post it, DM it, or generate a WhatsApp card in one click."/>
            <Step n={2} icon={TrendingUp} title="They upgrade to Pro" body="₹500 credited to your balance the moment they pay. Their first month gets a 3x-ROI guarantee too."/>
            <Step n={3} icon={Trophy} title="You unlock cash tier" body="Hit 5 paid referrals and your future rewards convert to cash payouts — direct to your bank."/>
          </div>
        </section>

        {/* Rewards */}
        <section className="max-w-6xl mx-auto px-4 sm:px-6 py-10">
          <div className="text-center mb-8">
            <div className="text-[11px] uppercase tracking-[0.32em] font-semibold text-[color:var(--sb-text-muted)]">Rewards</div>
            <h2 className="mt-2 font-serif-display font-bold text-3xl sm:text-4xl">Free Pro. Cash payouts. Leaderboard glory.</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Reward title="₹500 cash / paid referral" body="Credited instantly to your Referral Center wallet. Withdraw when you unlock the cash tier at 5 referrals." tone="emerald" icon={Gift}/>
            <Reward title="Free Pro month" body="Every paid referral earns you one free month of Pro. Stack them up — many users go 6+ months free." tone="saffron" icon={Sparkles}/>
            <Reward title="Public leaderboard" body="Opt-in leaderboard on the /trends page. Top referrers get 'SociBaba Advocate' badge + revenue share." tone="fuchsia" icon={Trophy}/>
          </div>
        </section>

        {referrer && (
          <section className="max-w-3xl mx-auto px-4 sm:px-6 py-8 text-center">
            <div className="rounded-2xl border border-[color:var(--sb-border)] bg-[color:var(--sb-surface)] p-6" data-testid="ref-stats">
              <div className="text-[10px] uppercase tracking-[0.24em] font-semibold text-[color:var(--sb-text-muted)]">Your friend's SociBaba stats</div>
              <div className="mt-3 grid grid-cols-3 gap-3">
                <div><div className="text-2xl font-serif-display font-bold text-[color:var(--sb-text)]">{referrer.paid_count || 0}</div><div className="text-[11px] text-[color:var(--sb-text-muted)]">paid referrals</div></div>
                <div><div className="text-2xl font-serif-display font-bold text-[color:var(--sb-text)]">{referrer.free_days_earned || 0}</div><div className="text-[11px] text-[color:var(--sb-text-muted)]">days earned</div></div>
                <div><div className="text-2xl font-serif-display font-bold text-[color:var(--sb-text)]">{referrer.cash_tier_unlocked ? "Yes" : "Building"}</div><div className="text-[11px] text-[color:var(--sb-text-muted)]">cash tier</div></div>
              </div>
            </div>
          </section>
        )}

        {/* Final CTA */}
        <section className="max-w-4xl mx-auto px-4 sm:px-6 pt-6 pb-16 text-center">
          <Link to="/signup" data-testid="ref-final-cta" className="inline-flex items-center gap-2 rounded-full bg-[color:var(--sb-saffron,#F97316)] text-white px-8 py-4 text-lg font-semibold shadow-xl shadow-[color:var(--sb-saffron,#F97316)]/30 hover:bg-[color:var(--sb-saffron-hover,#EA580C)]">
            <MessageCircle className="h-5 w-5"/> Start referring today
          </Link>
        </section>
      </div>
    </HelmetProvider>
  );
}

function Step({ n, icon: Icon, title, body }: { n: number; icon: any; title: string; body: string }) {
  return (
    <div className="rounded-2xl border border-[color:var(--sb-border)] bg-[color:var(--sb-surface)] p-6 relative overflow-hidden">
      <div className="absolute top-3 right-4 text-6xl font-serif-display font-bold text-[color:var(--sb-saffron,#F97316)]/10">{n}</div>
      <div className="relative">
        <div className="h-10 w-10 rounded-xl bg-[color:var(--sb-cream,#F4F1EA)] flex items-center justify-center text-[color:var(--sb-saffron,#F97316)]">
          <Icon className="h-5 w-5" strokeWidth={2.2}/>
        </div>
        <div className="mt-3 font-serif-display text-xl font-bold text-[color:var(--sb-text)]">{title}</div>
        <div className="mt-1 text-sm text-[color:var(--sb-text-muted)] leading-relaxed">{body}</div>
      </div>
    </div>
  );
}

function Reward({ title, body, tone, icon: Icon }: { title: string; body: string; tone: string; icon: any }) {
  const toneMap: Record<string, string> = {
    emerald: "from-emerald-500/15 to-emerald-500/0 text-emerald-700 dark:text-emerald-300",
    saffron: "from-[color:var(--sb-saffron,#F97316)]/15 to-transparent text-[color:var(--sb-saffron,#F97316)]",
    fuchsia: "from-fuchsia-500/15 to-fuchsia-500/0 text-fuchsia-600 dark:text-fuchsia-300",
  };
  return (
    <div className={`rounded-2xl border border-[color:var(--sb-border)] bg-gradient-to-br ${toneMap[tone]} p-6`}>
      <Icon className="h-6 w-6" strokeWidth={2.5}/>
      <div className="mt-3 font-serif-display text-xl font-bold text-[color:var(--sb-text)]">{title}</div>
      <div className="mt-1 text-sm text-[color:var(--sb-text-muted)] leading-relaxed">{body}</div>
    </div>
  );
}
